import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getLatestBriefingConditional, generateBriefing, saveItem } from '../services/dailyBriefing';
import { getAllDigestHighlights } from '../services/highlights';
import { useToast } from '../hooks/useToast';
import { useLocalFirstQuery } from '../hooks/useLocalFirstQuery';
import useLocalMutation from '../hooks/useLocalMutation';
import useOverlayedMutations from '../hooks/useOverlayedMutations';
import { overlayDaily } from '../db/overlay';
import { BRIEFING_TTL_MS } from '../db/freshness';
import { setTitle } from '../utils/environment';
import { formatRelativeDate } from '../utils/dateUtils';
import { renderDigestMarkdown } from '../utils/markdown';
import { ArticleLink } from '../components/SmartLink';

const SOURCE_BADGES = {
  hackernews: { label: 'HN', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  github: { label: 'GitHub', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  websearch: { label: 'Web', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  rss: { label: 'RSS', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  manual: null,
};

const SourceBadge = ({ sourceType, sourceLabel }) => {
  const badge = SOURCE_BADGES[sourceType];
  if (badge === null) return null;
  if (!badge && !sourceLabel) return null;
  const resolved = badge || { label: sourceType, className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${resolved.className}`}>
      {sourceLabel || resolved.label}
    </span>
  );
};

const SaveButton = ({ saveState, onClick, title }) => (
  <button
    onClick={onClick}
    disabled={saveState === true || saveState === 'done'}
    className={`flex-shrink-0 p-2 rounded-clay border-2 transition-all ${
      saveState === 'done'
        ? 'border-fun-mint bg-emerald-50 dark:bg-emerald-950/30 text-fun-mint'
        : 'border-theme-border-subtle text-tertiary-text hover:text-teal-600 hover:border-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30'
    } disabled:opacity-50 disabled:cursor-not-allowed`}
    title={saveState === 'done' ? 'Saved' : (title || 'Save to reading list')}
  >
    {saveState === true ? (
      <div className="spinner-fun-sm w-4 h-4"></div>
    ) : saveState === 'done' ? (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
      </svg>
    )}
  </button>
);

const deriveMarkedAsRead = (data) => {
  const result = {};
  (data.themes || []).forEach((theme, idx) => {
    const allItems = [
      ...(theme.whats_new || []),
      ...(theme.worth_saving || []),
    ].filter(item => item.source_article_id);
    if (allItems.length > 0 && allItems.every(item => item.is_read)) {
      result[idx] = true;
    }
  });
  return result;
};

const DailyBriefing = () => {
  const { showSuccess, showError } = useToast();
  const articleNavContext = { source: 'daily-briefing' };
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [expandedThemes, setExpandedThemes] = useState({});
  const [savingItems, setSavingItems] = useState({});
  const [digestHighlights, setDigestHighlights] = useState([]);
  const [highlightingItem, setHighlightingItem] = useState(null);
  const [markingInProgress, setMarkingInProgress] = useState({});
  const prevBriefingRef = useRef(null);
  const { mutate } = useLocalMutation();
  const { mutations: pendingReads } = useOverlayedMutations({
    types: ['mark_read', 'mark_unread', 'mark_all_read'],
  });

  const fetchFn = useCallback(async ({ etag }) => {
    return getLatestBriefingConditional({ etag });
  }, []);

  const toRecord = useCallback((payload, meta) => ({
    date: meta.key,
    payload,
    etag: meta.etag,
    fetched_at: meta.fetched_at,
    content_hash: meta.content_hash,
  }), []);

  const { data: briefing, loading, error: fetchError, refresh } = useLocalFirstQuery({
    table: 'daily_briefings',
    key: 'latest',
    ttl: BRIEFING_TTL_MS,
    fetch: fetchFn,
    toRecord,
  });

  const briefingView = useMemo(
    () => overlayDaily(briefing, pendingReads),
    [briefing, pendingReads],
  );

  // Derive per-theme read state from the overlaid briefing so pending mark_read
  // mutations are reflected immediately without waiting for a server round-trip.
  const markedAsRead = useMemo(
    () => (briefingView ? deriveMarkedAsRead(briefingView) : {}),
    [briefingView],
  );

  const error = genError || fetchError?.message || null;

  const fetchDigestHighlights = async () => {
    try {
      const response = await getAllDigestHighlights();
      const allHighlights = [];
      if (response.topics) {
        Object.values(response.topics).forEach(topicData => {
          if (topicData.highlights) {
            allHighlights.push(...topicData.highlights);
          }
        });
      }
      setDigestHighlights(allHighlights);
    } catch (err) {
      console.error('Failed to fetch digest highlights:', err);
    }
  };

  const isDigestItemHighlighted = (digestText) => {
    return digestHighlights.some(h => h.exact_text === digestText);
  };

  const getThemeArticleIds = (theme) => {
    const ids = new Set();
    (theme.whats_new || []).forEach(item => {
      if (item.source_article_id) ids.add(item.source_article_id);
    });
    (theme.worth_saving || []).forEach(item => {
      if (item.source_article_id) ids.add(item.source_article_id);
    });
    return [...ids];
  };

  const handleMarkAllRead = async (e, themeIdx) => {
    e.stopPropagation();
    if (markedAsRead[themeIdx] || markingInProgress[themeIdx]) return;

    const theme = (briefingView ?? briefing).themes[themeIdx];
    const ids = getThemeArticleIds(theme);
    if (ids.length === 0) return;

    setMarkingInProgress(prev => ({ ...prev, [themeIdx]: true }));
    try {
      // Reason: daily briefing has no topic_id; backend mark_all_read does a
      // topic DB lookup that would fail for a sentinel. Emit one mark_read per
      // article instead — still goes through mutation_log for offline support.
      await Promise.all(ids.map(id => mutate({ type: 'mark_read', payload: { article_id: id } })));
    } catch (err) {
      console.error('Mark-all-read failed:', err);
    } finally {
      setMarkingInProgress(prev => ({ ...prev, [themeIdx]: false }));
    }
  };

  const handleHighlightItem = async (e, text, section, articleId = null) => {
    e.stopPropagation();
    const itemKey = `briefing-${section}-${text}`;
    if (highlightingItem === itemKey) return;

    setHighlightingItem(itemKey);
    try {
      await mutate({
        type: 'digest_highlight_create',
        payload: {
          article_id: articleId,
          topic_name: `daily-briefing-${briefing?.briefing_date}`,
          digest_section: section,
          digest_text: text,
          digest_version: briefing?.generated_at,
        },
      });
      await fetchDigestHighlights();
    } catch (err) {
      console.error('Failed to highlight item:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setHighlightingItem(null);
    }
  };

  // Expand all themes when new briefing data arrives.
  useEffect(() => {
    if (!briefing || briefing === prevBriefingRef.current) return;
    prevBriefingRef.current = briefing;
    const expanded = {};
    (briefing.themes || []).forEach((_, idx) => { expanded[idx] = true; });
    setExpandedThemes(expanded);
  }, [briefing]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setGenError(null);
      await generateBriefing({ force: !!briefing });
      await refresh();
      showSuccess('Briefing generated!');
    } catch (err) {
      setGenError(err.message);
      showError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const toggleTheme = (idx) => {
    setExpandedThemes(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSaveItem = async (themeIndex, section, itemIndex) => {
    const key = `${themeIndex}-${section}-${itemIndex}`;
    if (savingItems[key]) return;

    setSavingItems(prev => ({ ...prev, [key]: true }));
    try {
      const result = await saveItem(briefing.id, themeIndex, section, itemIndex);
      const action = result.action === 'promoted' ? 'Added to reading list' : 'Saved and processing';
      showSuccess(action);
      setSavingItems(prev => ({ ...prev, [key]: 'done' }));
    } catch (err) {
      showError(err.message);
      setSavingItems(prev => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    setTitle('Daily Briefing · Read It Never');
    fetchDigestHighlights();
  }, []);

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-surface border-b-3 border-clay-border shadow-clay-sm">
        <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/dashboard" className="btn-clay-ghost mr-4 p-2" aria-label="Back to dashboard">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-heading flex items-center gap-2">
                  <span role="img" aria-hidden="true">☀️</span>
                  Daily Briefing
                </h1>
                <p className="text-sm text-secondary-text mt-1">
                  {briefing
                    ? `${briefing.article_count || briefing.themes?.length || 0} sources · Updated ${formatRelativeDate(briefing.generated_at || briefing.briefing_date)}`
                    : 'Your curated news across the web'}
                </p>
              </div>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-clay text-sm font-medium"
            >
              {generating ? (
                <>
                  <div className="spinner-fun-sm w-4 h-4 mr-2 inline-block"></div>
                  Generating...
                </>
              ) : (
                'Generate New'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-950/30 border-3 border-fun-coral rounded-clay flex items-center gap-2">
            <span className="text-xl" role="img" aria-hidden="true">😅</span>
            <span className="text-rose-700 dark:text-rose-300 font-medium">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="spinner-fun-lg mx-auto mb-4"></div>
              <p className="font-display text-lg text-secondary-text">Loading briefing...</p>
            </div>
          </div>
        ) : !briefing ? (
          <div className="card-clay-static p-12 text-center">
            <div className="text-6xl mb-4 animate-bounce-gentle">☀️</div>
            <h2 className="font-display text-2xl font-bold text-heading mb-2">No briefing yet</h2>
            <p className="text-secondary-text mb-6 max-w-md mx-auto">
              Generate a daily briefing to get a curated overview of news from Hacker News, GitHub trending, and web sources.
            </p>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-clay inline-flex items-center"
            >
              {generating ? 'Generating...' : 'Generate Briefing'}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            {briefing.summary && (
              <div className="card-clay-static p-4 sm:p-6 border-l-4 border-l-teal-400">
                <p className="text-body-text leading-relaxed">{briefing.summary}</p>
              </div>
            )}

            {/* Theme Cards */}
            {(briefingView ?? briefing).themes?.map((theme, themeIdx) => (
              <div
                key={theme.name || themeIdx}
                className="card-clay-static !p-0 overflow-hidden"
                style={{ animationDelay: `${themeIdx * 100}ms` }}
              >
                {/* Theme Header */}
                <div
                  className="px-2 py-3 sm:p-4 border-b-2 border-clay-border cursor-pointer hover:bg-surface-hover transition-colors"
                  onClick={() => toggleTheme(themeIdx)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className={`text-lg text-tertiary-text transition-transform duration-200 flex-shrink-0 ${
                        expandedThemes[themeIdx] ? 'rotate-0' : '-rotate-90'
                      }`}>
                        ▼
                      </span>
                      <h2 className="font-display text-lg font-semibold text-heading min-w-0 break-words">
                        {theme.name}
                      </h2>
                      <span className="badge-fun-sky text-xs flex-shrink-0">
                        {theme.whats_new?.length || 0} insights
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleMarkAllRead(e, themeIdx)}
                      disabled={markedAsRead[themeIdx] || markingInProgress[themeIdx] || getThemeArticleIds(theme).length === 0}
                      className={`p-2 rounded-clay border-2 transition-all ${
                        markedAsRead[themeIdx]
                          ? 'border-fun-mint bg-emerald-50 dark:bg-emerald-950/30 text-fun-mint'
                          : 'border-theme-border-subtle text-tertiary-text hover:text-fun-mint hover:border-fun-mint hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                      title={markedAsRead[themeIdx] ? 'All read' : 'Mark all as read'}
                    >
                      {markingInProgress[themeIdx] ? (
                        <div className="spinner-fun-sm w-5 h-5"></div>
                      ) : markedAsRead[themeIdx] ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Theme Content — 3 sections */}
                {expandedThemes[themeIdx] && (
                  <div className="px-2 py-4 sm:p-6 space-y-6">

                    {/* What's New */}
                    {theme.whats_new && theme.whats_new.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">📰</span>
                          What&apos;s New
                        </h3>
                        <ul className="space-y-2">
                          {theme.whats_new.map((item, itemIdx) => {
                            const isHighlighted = isDigestItemHighlighted(item.text);
                            const itemKey = `briefing-whats_new-${item.text}`;
                            const isHighlighting = highlightingItem === itemKey;
                            return (
                              <li
                                key={itemIdx}
                                className={`group text-sm text-body-text leading-relaxed pl-2 sm:pl-4 border-l-2 ${
                                  isHighlighted ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 py-2 pr-2 rounded-r' : 'border-blue-300'
                                }`}
                              >
                                <div className="flex items-start justify-between w-full">
                                  <div className="flex-1">
                                    {renderDigestMarkdown(item.text, item.source_article_id, articleNavContext)}
                                    <SourceBadge sourceType={item.source_type} sourceLabel={item.source_label} />
                                  </div>
                                  <button
                                    onClick={(e) => handleHighlightItem(e, item.text, 'whats_new', item.source_article_id)}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 cursor-default'
                                        : 'text-tertiary-text hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Analysis */}
                    {theme.analysis && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">🔍</span>
                          Analysis
                        </h3>
                        <div className="text-secondary-text leading-relaxed border-l-3 border-teal-300 pl-4 space-y-3">
                          {theme.analysis.split('\n\n').map((paragraph, pIdx) => (
                            <p key={pIdx}>{renderDigestMarkdown(paragraph)}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Worth Saving */}
                    {theme.worth_saving && theme.worth_saving.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">⭐</span>
                          Worth Saving
                        </h3>
                        <div className="space-y-2">
                          {theme.worth_saving.map((item, itemIdx) => {
                            const saveKey = `${themeIdx}-worth_saving-${itemIdx}`;
                            const saveState = savingItems[saveKey];
                            return (
                              <div
                                key={itemIdx}
                                className="group p-3 rounded-clay border-2 border-clay-border hover:border-teal-300 hover:shadow-clay-sm transition-all"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      {item.source_article_id ? (
                                        <ArticleLink
                                          articleId={item.source_article_id}
                                          context={articleNavContext}
                                          onClick={(e) => e.stopPropagation()}
                                          className="font-medium text-heading hover:text-primary-600 transition-colors text-left no-underline"
                                        >
                                          {item.title}
                                        </ArticleLink>
                                      ) : (
                                        <a
                                          href={item.source_url?.startsWith('http') ? item.source_url : '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="font-medium text-heading hover:text-primary-600 transition-colors"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {item.title}
                                        </a>
                                      )}
                                      <SourceBadge sourceType={item.source_type} sourceLabel={item.source_label} />
                                    </div>
                                    <p className="text-secondary-text leading-relaxed italic">
                                      {renderDigestMarkdown(item.reason)}
                                    </p>
                                  </div>
                                  <SaveButton
                                    saveState={saveState}
                                    onClick={() => handleSaveItem(themeIdx, 'worth_saving', itemIdx)}
                                    title="Save article to reading list"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyBriefing;
